/**
 * Tablas EDITABLES dentro del editor.
 *
 * Una tabla es lo único del markdown que no se puede "insinuar" con
 * decoraciones: alineada a mano con pipes es ilegible, y editarla así es peor.
 * Acá se dibuja la tabla de verdad y se escribe ADENTRO de ella —las celdas son
 * editables— así que en la hoja los pipes no aparecen nunca. El texto crudo
 * sigue estando, claro, pero se mira en el modo crudo, que es para eso.
 *
 * El `.md` no cambia de naturaleza: cada tecleo se escribe de vuelta como la
 * misma tabla GFM de siempre, que cualquier otro editor abre igual. Lo que sí
 * cambia al editarla es el ESPACIADO de la tabla —se normaliza a `| a | b |`—
 * porque conservar el alineado a mano celda por celda no vale lo que cuesta.
 *
 * Tres decisiones que no son obvias:
 *
 *  - Las tablas se buscan con un escaneo de texto y no en el árbol de sintaxis.
 *    El árbol sólo está parseado cerca de lo visible, así que una tabla lejana
 *    aparecería cruda hasta que el parser llegue; el escaneo no tiene ese
 *    problema y además no depende de qué extensiones del parser estén prendidas.
 *  - Va en un StateField y no en el ViewPlugin del Live Preview porque
 *    CodeMirror prohíbe que un plugin aporte decoraciones de bloque.
 *  - El widget es dueño de su DOM y de sus eventos (`ignoreEvent`,
 *    `ignoreMutation`): CodeMirror no tiene que enterarse de lo que pasa
 *    adentro de una celda, y el widget le avisa despachando los cambios.
 *    `updateDOM` es lo que evita que el cursor se pierda: cuando el cambio vino
 *    de acá adentro, el DOM ya está bien y no se vuelve a dibujar.
 */

import {
  type Command,
  Decoration,
  type DecorationSet,
  EditorView,
  WidgetType,
} from "@codemirror/view";
import {
  type EditorState,
  type Extension,
  type Range,
  StateField,
  type Text,
} from "@codemirror/state";
import { redo, undo } from "@codemirror/commands";
import { renderMarkdownInline } from "../render/markdown.ts";
import { encodeTarget, parseWikiLink } from "../vault/links.ts";
import { openLinkDialog } from "../app/dialogs.ts";
import { floatLinkCard, isMissing, linkMarkdown, type LinkUIOptions } from "./linkUI.ts";

/** La fila de guiones: `| --- | :---: |`. Tiene que tener al menos un guión. */
const DELIM_RE = /^\s{0,3}\|?[\s:|-]*-[\s:|-]*$/;
/** Apertura o cierre de un bloque de código cercado. */
const FENCE_RE = /^\s{0,3}(```|~~~)/;

type Align = "" | "left" | "right" | "center";

interface TableModel {
  header: string[];
  align: Align[];
  rows: string[][];
}

interface TableBlock {
  from: number;
  to: number;
  src: string;
}

// ── Fuente ↔ modelo ─────────────────────────────────────────────────────────

/**
 * Las celdas de una fila.
 *
 * `| a | b |` y `a | b` son las dos formas válidas: se parte por los pipes que
 * no estén escapados y se descartan los extremos vacíos, que es lo mismo que
 * hace markdown-it al renderizar.
 */
function splitCells(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === "\\" && line[i + 1] === "|") {
      cur += "|";
      i++;
    } else if (ch === "|") {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  if (out.length > 1 && out[0].trim() === "") out.shift();
  if (out.length > 1 && out[out.length - 1].trim() === "") out.pop();
  return out.map((c) => c.trim());
}

function alignOf(spec: string): Align {
  const s = spec.trim();
  const left = s.startsWith(":");
  const right = s.endsWith(":");
  if (left && right) return "center";
  if (right) return "right";
  if (left) return "left";
  return "";
}

function alignSpec(a: Align): string {
  if (a === "center") return ":---:";
  if (a === "right") return "---:";
  if (a === "left") return ":---";
  return "---";
}

function parseTable(src: string): TableModel | null {
  const lines = src.split("\n");
  if (lines.length < 2) return null;
  const header = splitCells(lines[0]);
  const align = splitCells(lines[1]).map(alignOf);
  if (header.length === 0 || align.length === 0) return null;
  const rows = lines.slice(2).map((l) => splitCells(l));
  // Todas las filas se llevan al ancho del encabezado: una fila corta deja
  // celdas vacías en vez de desarmar la grilla.
  const width = header.length;
  const fit = (cells: string[]): string[] =>
    Array.from({ length: width }, (_, i) => cells[i] ?? "");
  return {
    header: fit(header),
    align: Array.from({ length: width }, (_, i) => align[i] ?? ""),
    rows: rows.map(fit),
  };
}

/** Una celda escrita como texto de tabla: sin pipes sueltos ni saltos de línea. */
function escapeCell(text: string): string {
  return text.replace(/\r?\n/g, " ").replace(/\|/g, "\\|").trim();
}

function serialize(model: TableModel): string {
  const row = (cells: string[]): string => `| ${cells.map(escapeCell).join(" | ")} |`;
  return [
    row(model.header),
    `| ${model.align.map(alignSpec).join(" | ")} |`,
    ...model.rows.map(row),
  ].join("\n");
}

/** La tabla con la que arranca `/tabla`: dos filas para empezar a escribir. */
export function emptyTable(cols = 3): string {
  const model: TableModel = {
    header: Array.from({ length: cols }, (_, i) => `Columna ${i + 1}`),
    align: Array.from({ length: cols }, () => "" as Align),
    rows: [Array.from({ length: cols }, () => "")],
  };
  return serialize(model);
}

/**
 * Pedido de foco pendiente: la posición de una tabla recién insertada, para
 * dejar el cursor dentro de su primera celda apenas se dibuja.
 */
let focusRequest: number | null = null;

/**
 * Inserta una tabla vacía y deja el cursor en su primera celda.
 *
 * Lo usa `/tabla` del menú de bloques. Se escribe markdown común —la misma
 * tabla GFM que escribiría uno a mano—, no un formato propio.
 */
export const insertTable: Command = (view) => {
  const { state } = view;
  const pos = state.selection.main.head;
  const line = state.doc.lineAt(pos);
  // Una tabla necesita empezar su propio bloque: si la línea tiene algo escrito,
  // se abre una nueva.
  const prefix = line.text.slice(0, pos - line.from).trim() === "" ? "" : "\n";
  const insert = `${prefix}${emptyTable()}\n`;
  // Antes del dispatch: CodeMirror dibuja la tabla dentro de esa misma llamada,
  // así que el widget tiene que encontrar el pedido de foco ya puesto.
  focusRequest = pos + prefix.length;
  view.dispatch({
    changes: { from: pos, insert },
    selection: { anchor: pos + insert.length },
    userEvent: "input",
  });
  return true;
};

// ── Búsqueda de tablas en el documento ──────────────────────────────────────

/**
 * Los bloques que son una tabla GFM: una fila con pipes seguida de una fila de
 * guiones, y todas las filas con pipes que vengan abajo.
 */
function findTables(doc: Text): TableBlock[] {
  const text = doc.toString();
  if (!text.includes("|")) return [];
  const lines = text.split("\n");
  const out: TableBlock[] = [];
  const offsets: number[] = [];
  let at = 0;
  for (const line of lines) {
    offsets.push(at);
    at += line.length + 1;
  }

  let fenced = false;
  for (let i = 0; i < lines.length; i++) {
    if (FENCE_RE.test(lines[i])) {
      fenced = !fenced;
      continue;
    }
    if (fenced) continue;
    const head = lines[i];
    const delim = lines[i + 1];
    if (!head.includes("|") || delim === undefined) continue;
    if (!DELIM_RE.test(delim) || !delim.includes("-")) continue;
    // Mismo criterio que GFM: el encabezado y la fila de guiones tienen que
    // tener la misma cantidad de columnas, si no no es una tabla.
    if (splitCells(head).length !== splitCells(delim).length) continue;

    let end = i + 1;
    while (end + 1 < lines.length) {
      const next = lines[end + 1];
      if (next.trim() === "" || !next.includes("|") || FENCE_RE.test(next)) break;
      end++;
    }
    const from = offsets[i];
    const to = offsets[end] + lines[end].length;
    out.push({ from, to, src: text.slice(from, to) });
    i = end;
  }
  return out;
}

// ── El widget ───────────────────────────────────────────────────────────────

/**
 * Lo que la tabla necesita del documento. Son las mismas opciones de enlace que
 * usa la hoja: un enlace en una celda tiene que verse y tocarse igual que uno
 * en un párrafo, o hay dos sistemas de enlaces conviviendo.
 */
export type TableOptions = LinkUIOptions;

/** Un enlace escrito en una celda: `[texto](destino)` o `[[wikilink]]`. */
const CELL_LINK_RE = /\[\[([^[\]\n]+)\]\]|\[([^\]]*)\]\(([^)]*)\)/g;

interface CellLink {
  from: number;
  to: number;
  text: string;
  href: string;
}

/**
 * El texto fuente de una celda, leído del DOM.
 *
 * Una celda en edición no es texto plano: los enlaces siguen siendo piezas
 * (`<a data-md>`), así que se los traduce de vuelta a su markdown exacto —el
 * que estaba escrito, wikilink incluido— en vez de leer lo que se ve.
 */
function readCell(cell: HTMLElement): string {
  let out = "";
  for (const node of cell.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) out += node.nodeValue ?? "";
    else if (node instanceof HTMLElement) out += node.dataset.md ?? node.textContent ?? "";
    else out += node.textContent ?? "";
  }
  return out;
}

/** Los enlaces de una celda, en el mismo orden en que se dibujan. */
function cellLinks(src: string): CellLink[] {
  const out: CellLink[] = [];
  for (const m of src.matchAll(CELL_LINK_RE)) {
    const from = m.index ?? 0;
    const to = from + m[0].length;
    if (m[1] !== undefined) {
      const { target, label } = parseWikiLink(m[1]);
      out.push({ from, to, text: label, href: encodeTarget(target) });
    } else {
      out.push({ from, to, text: m[2] ?? "", href: (m[3] ?? "").trim() });
    }
  }
  return out;
}

/** El modelo vigente de cada tabla dibujada, para saber qué escribir al `.md`. */
const models = new WeakMap<HTMLElement, TableModel>();

const TOOL_ICONS: Record<string, string> = {
  addRow: "+ fila",
  addCol: "+ columna",
  delRow: "− fila",
  delCol: "− columna",
};

class TableWidget extends WidgetType {
  constructor(
    readonly src: string,
    private opts: TableOptions,
  ) {
    super();
  }

  eq(other: TableWidget): boolean {
    return other.src === this.src;
  }

  /**
   * El widget no se vuelve a dibujar si el DOM ya dice lo mismo que el `.md`.
   *
   * Es lo que hace que se pueda escribir: al teclear en una celda se despacha
   * el cambio al documento, CodeMirror rehace las decoraciones y, sin esto,
   * reemplazaría la tabla entera por una nueva —perdiendo el foco y el cursor
   * en cada letra—. Cuando el cambio vino de afuera (deshacer, una edición en
   * modo crudo), el DOM sí se redibuja.
   */
  updateDOM(dom: HTMLElement, view: EditorView): boolean {
    const model = models.get(dom);
    if (model && serialize(model) === this.src) return true;
    const fresh = parseTable(this.src);
    if (!fresh) return false;
    this.render(dom, fresh, view);
    return true;
  }

  toDOM(view: EditorView): HTMLElement {
    const wrap = document.createElement("div");
    wrap.className = "cm-lp-table";
    const model = parseTable(this.src);
    if (model) this.render(wrap, model, view);

    // Una tabla recién creada con `/tabla` arranca con el cursor adentro: es
    // una tabla vacía, no hay nada que leer en ella.
    if (focusRequest !== null) {
      setTimeout(() => {
        if (focusRequest === null || !wrap.isConnected) return;
        if (Math.abs(view.posAtDOM(wrap) - focusRequest) > 1) return;
        focusRequest = null;
        const first = wrap.querySelector<HTMLElement>("th, td");
        if (!first) return;
        first.focus();
        // El texto de muestra ("Columna 1") queda seleccionado: escribir lo
        // reemplaza, que es lo que uno hace con un encabezado recién creado.
        const range = document.createRange();
        range.selectNodeContents(first);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }, 0);
    }
    return wrap;
  }

  ignoreEvent(): boolean {
    return true;
  }

  ignoreMutation(): boolean {
    return true;
  }

  // ── Dibujo ────────────────────────────────────────────────────────────────

  private render(wrap: HTMLElement, model: TableModel, view: EditorView): void {
    models.set(wrap, model);
    wrap.replaceChildren();

    const table = document.createElement("table");
    const thead = document.createElement("thead");
    thead.append(this.buildRow(wrap, model, model.header, true, view));
    const tbody = document.createElement("tbody");
    for (const row of model.rows) tbody.append(this.buildRow(wrap, model, row, false, view));
    table.append(thead, tbody);

    wrap.append(table, this.buildTools(wrap, view));
  }

  private buildRow(
    wrap: HTMLElement,
    model: TableModel,
    cells: string[],
    head: boolean,
    view: EditorView,
  ): HTMLTableRowElement {
    const tr = document.createElement("tr");
    cells.forEach((text, i) => {
      const cell = document.createElement(head ? "th" : "td");
      // `plaintext-only` evita que pegar traiga HTML y que ⌘B meta un <b>: una
      // celda es texto markdown, no un documento con formato propio.
      cell.setAttribute("contenteditable", "plaintext-only");
      cell.spellcheck = false;
      cell.dataset.col = String(i);
      if (model.align[i]) cell.style.textAlign = model.align[i];
      this.paint(cell, text);
      tr.append(cell);
    });
    this.wire(wrap, tr, view);
    return tr;
  }

  /**
   * La celda mientras se la edita: su texto tal cual está escrito —con sus
   * `**` a la vista, que es lo que deja corregirlos— PERO con los enlaces
   * todavía como piezas.
   *
   * Antes acá se volcaba el markdown entero y clickear un enlace terminaba
   * mostrando `[texto](destino)`, justo lo que el resto de la app dejó de
   * hacer. Un enlace no se escribe a mano en ningún lado: se toca desde su
   * ficha.
   */
  private paintSource(cell: HTMLElement, text: string): void {
    cell.dataset.src = text;
    cell.replaceChildren();
    let at = 0;
    for (const link of cellLinks(text)) {
      if (link.from > at) cell.append(text.slice(at, link.from));
      const a = document.createElement("a");
      a.href = link.href;
      a.textContent = link.text;
      // El markdown exacto viaja con la pieza: así se vuelve a escribir igual
      // (un `[[wikilink]]` importado no se convierte solo).
      a.dataset.md = text.slice(link.from, link.to);
      a.setAttribute("contenteditable", "false");
      a.setAttribute("draggable", "false");
      cell.append(a);
      at = link.to;
    }
    if (at < text.length) cell.append(text.slice(at));
  }

  /** Fuera de foco la celda se ve renderizada; al entrar, se ve su texto. */
  private paint(cell: HTMLElement, text: string): void {
    cell.dataset.src = text;
    if (text === "") {
      cell.textContent = "";
      return;
    }
    // markdown-it con `html: false`: lo que escribió el usuario no puede
    // inyectar etiquetas.
    cell.innerHTML = renderMarkdownInline(text);

    // Un enlace adentro de una celda editable es una ISLA: `contenteditable`
    // en falso hace que el navegador no ponga el cursor ahí ni abra la celda a
    // editar cuando se lo clickea. Es lo que evita que tocar un enlace destape
    // el markdown de toda la celda.
    for (const a of cell.querySelectorAll("a")) {
      a.setAttribute("contenteditable", "false");
      a.setAttribute("draggable", "false");
      if (
        a.hasAttribute("data-internal") &&
        isMissing(a.getAttribute("href") ?? "", this.opts.resolveLink)
      ) {
        a.classList.add("md-link-missing");
      }
    }
    for (const img of cell.querySelectorAll("img")) {
      const src = img.getAttribute("src") ?? "";
      if (/^(https?:|data:|blob:)/i.test(src)) continue;
      img.removeAttribute("src");
      img.className = "cm-lp-img cm-lp-img-missing";
    }
  }

  private wire(wrap: HTMLElement, tr: HTMLTableRowElement, view: EditorView): void {
    for (const cell of [...tr.children] as HTMLElement[]) {
      /**
       * Cuándo fue el último click sobre un enlace de esta celda.
       *
       * El `preventDefault` del mousedown ya debería alcanzar para que el
       * navegador no enfoque la celda, pero no todos los caminos de foco pasan
       * por ahí: si igual llega, se lo deshace. Clickear un enlace nunca abre
       * el markdown de la celda.
       */
      let clickedLink = 0;
      cell.addEventListener("focus", () => {
        if (Date.now() - clickedLink < 400) {
          cell.blur();
          return;
        }
        this.paintSource(cell, cell.dataset.src ?? "");
      });
      cell.addEventListener("blur", () => {
        this.commit(wrap, view);
        this.paint(cell, readCell(cell));
      });
      cell.addEventListener("input", () => this.commit(wrap, view));
      cell.addEventListener("keydown", (e) => this.onKey(e, wrap, cell, view));
      // ── Enlaces de una celda ────────────────────────────────────────────
      // Mismo trato que en la hoja: clickearlos navega y al pasar el mouse
      // aparece su ficha. El `<a>` de acá es DOM del widget y no texto del
      // documento, así que el hover del editor no lo ve: la ficha se muestra
      // desde acá, con el mismo constructor.
      //
      // El `preventDefault` es incondicional: si el navegador siguiera el
      // `href` cambiaría la URL de la SPA y con ella se perdería el permiso del
      // vault, dejando la app cerrada.
      const onLinkPress = (e: MouseEvent): boolean => {
        const a = (e.target as HTMLElement | null)?.closest?.("a");
        if (!a) return false;
        clickedLink = Date.now();
        e.preventDefault();
        e.stopPropagation(); // clickear un enlace no entra a editar la celda
        return true;
      };
      cell.addEventListener("mousedown", (e) => {
        if (onLinkPress(e)) this.opts.open?.(
          ((e.target as HTMLElement).closest("a") as HTMLElement).getAttribute("href") ?? "",
        );
      });
      // El `click` también se ataja: si algún navegador enfocara la celda en
      // este paso en vez del anterior, acá se corta igual (navegar ya navegó).
      cell.addEventListener("click", onLinkPress);

      let hoverTimer: number | null = null;
      let card: { dom: HTMLElement; close: () => void } | null = null;
      const closeCard = (): void => {
        card?.close();
        card = null;
      };
      cell.addEventListener("mouseover", (e) => {
        const a = (e.target as HTMLElement | null)?.closest?.("a");
        if (!a) return;
        if (hoverTimer !== null) clearTimeout(hoverTimer);
        hoverTimer = window.setTimeout(() => this.showCard(wrap, cell, a, view, (c) => (card = c)), 250);
      });
      cell.addEventListener("mouseout", (e) => {
        if (!(e.target as HTMLElement | null)?.closest?.("a")) return;
        if (hoverTimer !== null) clearTimeout(hoverTimer);
        hoverTimer = null;
        // Un rato para poder llegar a los botones; si el mouse entró en la
        // ficha, ella misma se cierra al salir.
        window.setTimeout(() => {
          if (card && !card.dom.matches(":hover")) closeCard();
        }, 200);
      });
    }
  }

  /** La ficha del enlace de una celda, con las mismas acciones que en la hoja. */
  private showCard(
    wrap: HTMLElement,
    cell: HTMLElement,
    a: HTMLElement,
    view: EditorView,
    keep: (c: { dom: HTMLElement; close: () => void } | null) => void,
  ): void {
    const links = cellLinks(cell.dataset.src ?? "");
    const idx = [...cell.querySelectorAll("a")].indexOf(a as HTMLAnchorElement);
    const link = links[idx];
    if (!link) return;

    const card = floatLinkCard(
      a,
      { text: link.text, href: link.href, missing: isMissing(link.href, this.opts.resolveLink) },
      {
        open: () => {
          card.close();
          this.opts.open?.(link.href);
        },
        edit: () => {
          card.close();
          void this.editCellLink(wrap, cell, idx, view);
        },
        remove: () => {
          card.close();
          this.replaceCellLink(wrap, cell, idx, link.text, view);
        },
      },
    );
    keep(card);
  }

  /** Cambia (o saca) el enlace n de una celda, escribiendo su markdown. */
  private replaceCellLink(
    wrap: HTMLElement,
    cell: HTMLElement,
    idx: number,
    insert: string,
    view: EditorView,
  ): void {
    const src = cell.dataset.src ?? "";
    const link = cellLinks(src)[idx];
    if (!link) return;
    const next = `${src.slice(0, link.from)}${insert}${src.slice(link.to)}`;
    if (cell === document.activeElement) this.paintSource(cell, next);
    else this.paint(cell, next);
    this.commit(wrap, view);
  }

  private async editCellLink(
    wrap: HTMLElement,
    cell: HTMLElement,
    idx: number,
    view: EditorView,
  ): Promise<void> {
    const link = cellLinks(cell.dataset.src ?? "")[idx];
    if (!link) return;
    const res = await openLinkDialog({
      text: link.text,
      href: link.href,
      targets: this.opts.targets(),
      existing: true,
    });
    if (!res) return;
    const insert =
      res.action === "remove"
        ? link.text
        : linkMarkdown(res.text, res.href, this.opts.docPath(), this.opts.targets());
    this.replaceCellLink(wrap, cell, idx, insert, view);
  }

  // ── Escritura al documento ────────────────────────────────────────────────

  /**
   * El tramo que ocupa esta tabla en el `.md`, leído del documento.
   *
   * Se busca en vez de recordarse: el documento se puede haber movido por
   * debajo (deshacer, una edición en modo crudo, otra tabla que creció) y
   * escribir contra una posición vieja pisaría texto ajeno. Acá el `.md` es
   * siempre la verdad; el modelo en memoria es sólo lo que se está dibujando.
   */
  private range(
    wrap: HTMLElement,
    view: EditorView,
  ): { from: number; to: number; src: string } | null {
    const at = view.posAtDOM(wrap);
    for (const t of findTables(view.state.doc)) {
      if (t.from <= at && t.to >= at) return t;
    }
    return null;
  }

  /** Lee el DOM, arma la tabla y la escribe en el `.md` si cambió. */
  private commit(wrap: HTMLElement, view: EditorView): void {
    const span = this.range(wrap, view);
    const model = span ? parseTable(span.src) : null;
    if (!span || !model) return;
    const rows = [...wrap.querySelectorAll("tr")];
    const read = (tr: Element): string[] =>
      [...tr.children].map((c) => {
        const el = c as HTMLElement;
        // La celda en foco muestra su texto; las demás, su render.
        const text = el === document.activeElement ? readCell(el) : (el.dataset.src ?? "");
        return escapeCell(text);
      });
    const next: TableModel = {
      header: rows[0] ? read(rows[0]) : model.header,
      align: model.align,
      rows: rows.slice(1).map(read),
    };
    const src = serialize(next);
    if (src === span.src) return;
    models.set(wrap, next);
    // La celda en foco guarda su texto nuevo, así `blur` no lo revierte.
    const active = document.activeElement as HTMLElement | null;
    if (active && wrap.contains(active)) active.dataset.src = readCell(active);
    view.dispatch({
      changes: { from: span.from, to: span.to, insert: src },
      userEvent: "input",
    });
  }

  // ── Teclado ───────────────────────────────────────────────────────────────

  private onKey(e: KeyboardEvent, wrap: HTMLElement, cell: HTMLElement, view: EditorView): void {
    const mod = e.metaKey || e.ctrlKey;
    if (mod && e.key.toLowerCase() === "z") {
      // El foco está en la celda, así que el atajo no llega al editor: se lo
      // pasamos a mano para que deshacer siga siendo uno solo en todo el documento.
      e.preventDefault();
      (e.shiftKey ? redo : undo)(view);
      view.focus();
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      this.leave(wrap, view, "after");
      return;
    }
    if (e.key === "Tab") {
      e.preventDefault();
      this.move(wrap, cell, e.shiftKey ? -1 : 1, view);
      return;
    }
    if (e.key === "Enter") {
      // Una celda es una línea: el salto lleva a la de abajo (y si no hay, la crea).
      e.preventDefault();
      const cells = [...wrap.querySelectorAll<HTMLElement>("th, td")];
      const cols = wrap.querySelectorAll("tr")[0]?.children.length ?? 1;
      const i = cells.indexOf(cell);
      if (i + cols >= cells.length) this.addRow(wrap, view);
      this.move(wrap, cell, cols, view);
      return;
    }
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      const cells = [...wrap.querySelectorAll<HTMLElement>("th, td")];
      const cols = wrap.querySelectorAll("tr")[0]?.children.length ?? 1;
      const i = cells.indexOf(cell);
      const next = e.key === "ArrowUp" ? i - cols : i + cols;
      e.preventDefault();
      if (next < 0) this.leave(wrap, view, "before");
      else if (next >= cells.length) this.leave(wrap, view, "after");
      else this.focusCell(cells[next]);
    }
  }

  /** Saca el cursor de la tabla, arriba o abajo, y se lo devuelve al editor. */
  private leave(wrap: HTMLElement, view: EditorView, side: "before" | "after"): void {
    const model = models.get(wrap);
    const from = view.posAtDOM(wrap);
    const at = side === "before" ? Math.max(0, from - 1) : from + (model ? serialize(model).length : 0);
    view.dispatch({ selection: { anchor: Math.min(at, view.state.doc.length) } });
    view.focus();
  }

  private move(wrap: HTMLElement, cell: HTMLElement, delta: number, view: EditorView): void {
    const cells = [...wrap.querySelectorAll<HTMLElement>("th, td")];
    const i = cells.indexOf(cell);
    const next = i + delta;
    if (next < 0) {
      this.leave(wrap, view, "before");
      return;
    }
    if (next >= cells.length) {
      // Tab en la última celda: una fila nueva, como en cualquier planilla.
      this.addRow(wrap, view);
      const grown = [...wrap.querySelectorAll<HTMLElement>("th, td")];
      this.focusCell(grown[Math.min(next, grown.length - 1)]);
      return;
    }
    this.focusCell(cells[next]);
  }

  private focusCell(cell: HTMLElement | undefined): void {
    if (!cell) return;
    cell.focus();
    const range = document.createRange();
    range.selectNodeContents(cell);
    range.collapse(false);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }

  // ── Filas y columnas ──────────────────────────────────────────────────────

  private edit(wrap: HTMLElement, view: EditorView, change: (m: TableModel) => void): void {
    const span = this.range(wrap, view);
    const model = span ? parseTable(span.src) : null;
    if (!span || !model) return;
    change(model);
    const src = serialize(model);
    if (src === span.src) return;
    models.set(wrap, model);
    view.dispatch({
      changes: { from: span.from, to: span.to, insert: src },
      userEvent: "input",
    });
    this.render(wrap, model, view);
  }

  private addRow(wrap: HTMLElement, view: EditorView): void {
    this.edit(wrap, view, (m) => m.rows.push(m.header.map(() => "")));
  }

  private buildTools(wrap: HTMLElement, view: EditorView): HTMLElement {
    const tools = document.createElement("div");
    tools.className = "cm-lp-table-tools";
    const make = (key: keyof typeof TOOL_ICONS, run: () => void): void => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "cm-lp-table-tool";
      b.textContent = TOOL_ICONS[key];
      // `mousedown` y no `click`: al apretar, el blur de la celda ya habría
      // movido el foco y el botón dejaría de saber sobre qué fila actuar.
      b.addEventListener("mousedown", (e) => {
        e.preventDefault();
        run();
      });
      tools.append(b);
    };

    /** Fila y columna donde está el cursor (o la última, si no hay foco). */
    const where = (): { row: number; col: number } => {
      const cell = document.activeElement as HTMLElement | null;
      const rows = [...wrap.querySelectorAll("tr")];
      if (!cell || !wrap.contains(cell) || !cell.closest("tr")) {
        // Sin cursor adentro, se agrega o se saca al final: es lo que espera
        // quien aprieta el botón sin haber elegido una celda.
        return { row: rows.length - 1, col: (rows[0]?.children.length ?? 1) - 1 };
      }
      const tr = cell.closest("tr")!;
      return { row: rows.indexOf(tr), col: [...tr.children].indexOf(cell) };
    };

    make("addRow", () =>
      this.edit(wrap, view, (m) => {
        const { row } = where();
        m.rows.splice(Math.max(0, row), 0, m.header.map(() => ""));
      }),
    );
    make("addCol", () =>
      this.edit(wrap, view, (m) => {
        const { col } = where();
        const at = Math.min(col + 1, m.header.length);
        m.header.splice(at, 0, "");
        m.align.splice(at, 0, "");
        for (const r of m.rows) r.splice(at, 0, "");
      }),
    );
    make("delRow", () =>
      this.edit(wrap, view, (m) => {
        const { row } = where();
        // El encabezado no se borra: una tabla sin encabezado no es una tabla
        // en markdown. Estando ahí, se borra la primera fila de datos.
        const at = Math.max(0, row - 1);
        if (m.rows.length > 0) m.rows.splice(at, 1);
      }),
    );
    make("delCol", () =>
      this.edit(wrap, view, (m) => {
        if (m.header.length <= 1) return;
        const { col } = where();
        m.header.splice(col, 1);
        m.align.splice(col, 1);
        for (const r of m.rows) r.splice(col, 1);
      }),
    );
    return tools;
  }
}

// ── Decoración ──────────────────────────────────────────────────────────────

function build(state: EditorState, opts: TableOptions): DecorationSet {
  const decos: Range<Decoration>[] = [];
  for (const t of findTables(state.doc)) {
    if (!parseTable(t.src)) continue;
    decos.push(
      Decoration.replace({ widget: new TableWidget(t.src, opts), block: true }).range(t.from, t.to),
    );
  }
  return Decoration.set(decos, true);
}

/**
 * Se construye una vez por vista (y no una por estado) para que cambiar de
 * modo o de documento no vaya dejando campos nuevos detrás.
 */
export function mdTables(opts: TableOptions): Extension {
  return StateField.define<DecorationSet>({
    create: (state) => build(state, opts),
    update(set, tr) {
      // Sólo el texto manda: la tabla se ve igual esté el cursor donde esté.
      return tr.docChanged ? build(tr.state, opts) : set;
    },
    provide: (f) => EditorView.decorations.from(f),
  });
}
